import { useEffect, useState } from "react";
import { auth, db } from "../services/firebase";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  updatePassword,
  signOut,
  updateProfile,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { User, Mail, Shield, Camera, LogOut, Save, Key, Edit3, X, GraduationCap, Phone, Check } from "lucide-react";

export default function Profile() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [newName, setNewName] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    has8Chars: false,
    hasLetter: false,
    hasNumber: false,
    hasSymbol: false,
  });
  const [uploading, setUploading] = useState(false);
  const [user, setUser] = useState(auth.currentUser);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    middleName: "",
    surname: "",
    lrn: "",
    gradeYearLevel: "",
    section: "",
    contactNumber: "",
  });
  const [profileErrors, setProfileErrors] = useState({});

  // 🔹 Load user data from Firestore
  useEffect(() => {
    const fetchUser = async () => {
      const current = auth.currentUser;
      if (!current) return navigate("/login");

      const ref = doc(db, "users", current.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setUserData({ uid: current.uid, email: current.email, ...data });
        setNewName(data.name || "");
        setProfileForm({
          firstName: data.firstName || "",
          middleName: data.middleName || "",
          surname: data.surname || "",
          lrn: data.lrn || "",
          gradeYearLevel: data.gradeYearLevel || "",
          section: data.section || "",
          contactNumber: data.contactNumber || "",
        });
      }
    };
    fetchUser();
  }, [navigate]);

  // 🔹 Update name
  const handleNameUpdate = async () => {
    if (!newName.trim()) return alert("Enter a valid name");
    const user = auth.currentUser;

    await updateProfile(user, { displayName: newName });
    await updateDoc(doc(db, "users", user.uid), {
      name: newName,
      updatedAt: serverTimestamp(),
    });

    alert("✅ Name updated successfully!");
  };

  // 🔹 Update password
  const handlePasswordChange = async () => {
    if (!currentPassword.trim()) {
      return alert("Please enter your current password");
    }
    if (!newPassword.trim()) {
      return alert("Please enter a new password");
    }
    if (!confirmPassword.trim()) {
      return alert("Please confirm your new password");
    }
    if (newPassword !== confirmPassword) {
      return alert("New passwords do not match");
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      return alert(passwordError);
    }

    const user = auth.currentUser;
    if (!user || !user.email) {
      return alert("User is not authenticated");
    }

    try {
      // Reauthenticate user before password change
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);
      alert("🔑 Password updated successfully!");

      // Clear form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Password change error:", error);

      // Handle specific Firebase Auth errors
      if (error.code === 'auth/wrong-password') {
        alert("❌ Current password is incorrect. Please verify your current password and try again.");
      } else if (error.code === 'auth/weak-password') {
        alert("❌ The new password is too weak. Please ensure it meets all security requirements.");
      } else if (error.code === 'auth/requires-recent-login') {
        alert("❌ For security reasons, please log out and log back in before changing your password.");
      } else if (error.code === 'auth/too-many-requests') {
        alert("❌ Too many failed attempts. Please wait a moment and try again.");
      } else {
        alert("❌ Error changing password: " + error.message);
      }
    }
  };

  // 🔹 Upload via Cloudinary (same as web)
  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "profilePictures");

      const res = await fetch(
        "https://api.cloudinary.com/v1_1/dse3b23i2/image/upload",
        { method: "POST", body: formData }
      );

      const data = await res.json();
      const imageUrl = data.secure_url;

      await updateDoc(doc(db, "users", auth.currentUser.uid), {
        profilePicture: imageUrl,
        updatedAt: serverTimestamp(),
      });

      await updateProfile(auth.currentUser, { photoURL: imageUrl });

      // Update local state and storage
      const updatedUser = { ...userData, profilePicture: imageUrl };
      setUserData(updatedUser);
      setUser(auth.currentUser);
      localStorage.setItem("currentUser", JSON.stringify(updatedUser));

      alert("✅ Profile updated!");
    } catch (err) {
      console.error("Upload failed:", err);
      alert("❌ Could not upload image: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  // 🔹 Validate profile field
  const validateProfileField = (field, value) => {
    const newErrors = { ...profileErrors };

    switch (field) {
      case "firstName":
      case "surname":
        if (!value.trim()) {
          newErrors[field] = "This field is required";
        } else if (value.length < 2) {
          newErrors[field] = "Must be at least 2 characters";
        } else {
          delete newErrors[field];
        }
        break;
      case "lrn":
        if (!value.trim()) {
          newErrors[field] = "LRN is required";
        } else if (!/^\\d{12}$/.test(value)) {
          newErrors[field] = "LRN must be exactly 12 digits";
        } else {
          delete newErrors[field];
        }
        break;
      case "gradeYearLevel":
        if (!value) {
          newErrors[field] = "Please select your grade/year level";
        } else {
          delete newErrors[field];
        }
        break;
      case "section":
        if (!value.trim()) {
          newErrors[field] = "Section is required";
        } else {
          delete newErrors[field];
        }
        break;
      case "contactNumber":
        if (!value.trim()) {
          newErrors[field] = "Contact number is required";
        } else if (!/^(\\+63|0)[9]\\d{9}$/.test(value)) {
          newErrors[field] = "Please enter a valid Philippine mobile number";
        } else {
          delete newErrors[field];
        }
        break;
      default:
        break;
    }

    setProfileErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔹 Validate password requirements
  const validatePassword = (password) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/[a-zA-Z]/.test(password)) {
      return "Password must contain at least one letter";
    }
    if (!/\\d/.test(password)) {
      return "Password must contain at least one number";
    }
    if (!/[^a-zA-Z0-9]/.test(password)) {
      return "Password must contain at least one symbol";
    }
    return null;
  };

  // 🔹 Handle profile form change
  const handleProfileChange = (field, value) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
    validateProfileField(field, value);
  };

  // 🔹 Update password strength checklist on input change
  useEffect(() => {
    const has8Chars = newPassword.length >= 8;
    const hasLetter = /[a-zA-Z]/.test(newPassword);
    const hasNumber = /\\d/.test(newPassword);
    const hasSymbol = /[^a-zA-Z0-9]/.test(newPassword);

    setPasswordStrength({ has8Chars, hasLetter, hasNumber, hasSymbol });
  }, [newPassword]);

  // 🔹 Update student profile
  const handleProfileUpdate = async () => {
    // Validate all fields
    const allFields = Object.keys(profileForm);
    const isValid = allFields.every(field => validateProfileField(field, profileForm[field]));

    if (!isValid) return;

    try {
      const userRef = doc(db, "users", auth.currentUser.uid);
      const displayName = `${profileForm.firstName} ${profileForm.surname}`;

      await updateDoc(userRef, {
        ...profileForm,
        name: displayName,
        updatedAt: serverTimestamp(),
      });

      // Update Firebase Auth display name
      await updateProfile(auth.currentUser, { displayName });

      // Update local state
      setUserData(prev => ({
        ...prev,
        ...profileForm,
        name: displayName,
      }));

      setEditingProfile(false);
      alert("✅ Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Failed to update profile. Please try again.");
    }
  };

  // 🔹 Logout
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login?logout=true");
  };

  // Loading state with skeleton
  if (!userData) {
    return (
      <div className="min-h-screen bg-neutral-50 animate-fade-in">
        <div className="container-fluid pt-6 pb-6">
          <div className="max-w-md mx-auto space-y-6">
            <div className="text-center">
              <div className="skeleton-avatar mx-auto mb-4"></div>
              <div className="skeleton-title w-32 mx-auto mb-2"></div>
              <div className="skeleton-text w-24 mx-auto"></div>
            </div>
            <div className="skeleton h-32 w-full rounded-xl"></div>
            <div className="skeleton h-32 w-full rounded-xl"></div>
            <div className="skeleton h-32 w-full rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 animate-fade-in">
      <div className="container-fluid pt-6 pb-6">
        <div className="max-w-md mx-auto lg:max-w-2xl xl:max-w-3xl space-y-6">
          {/* Profile Header */}
          <div className="text-center">
            <div className="relative inline-block mb-4">
              <img
                src={
                  userData.profilePicture ||
                  userData.photoURL ||
                  "/adminnew.jpg"
                }
                alt="Profile"
                className="w-20 h-20 rounded-2xl object-cover mx-auto shadow-lg border-4 border-orange-500"
              />
              <label
                htmlFor="profilePic"
                className="absolute -bottom-2 -right-2 glass-button p-2 rounded-xl cursor-pointer hover:bg-surface-200 transition-all duration-200"
              >
                <Camera className="w-4 h-4 text-neutral-700" />
              </label>
              <input
                id="profilePic"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleProfilePictureChange}
              />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-2">
              {userData.name || "Student"}
            </h1>
            <p className="text-neutral-600 capitalize">
              {userData.role || "Student"}
            </p>
          </div>

          {/* Account Information */}
          <div className="card">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-info-600" />
              Account Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="form-label">Email Address</label>
                <div className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg">
                  <Mail className="w-4 h-4 text-neutral-500" />
                  <span className="text-neutral-900">{userData.email}</span>
                </div>
              </div>
              <div>
                <label className="form-label">Role</label>
                <div className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg">
                  <Shield className="w-4 h-4 text-neutral-500" />
                  <span className="text-neutral-900 capitalize">{userData.role}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Student Profile Information */}
          {userData.role === "student" && (
            <div className="card">
              <h2 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-primary-600" />
                  Student Information
                </div>
                <button
                  onClick={() => setEditingProfile(!editingProfile)}
                  className="text-primary-600 hover:text-primary-700 p-2 rounded-lg hover:bg-primary-50 transition-colors"
                >
                  {editingProfile ? <X className="w-5 h-5" /> : <Edit3 className="w-5 h-5" />}
                </button>
              </h2>

              {!editingProfile ? (
                // Display mode
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label text-sm">First Name</label>
                      <div className="p-3 bg-neutral-50 rounded-lg">
                        <span className="text-neutral-900">{userData.firstName || "Not set"}</span>
                      </div>
                    </div>
                    <div>
                      <label className="form-label text-sm">Middle Name</label>
                      <div className="p-3 bg-neutral-50 rounded-lg">
                        <span className="text-neutral-900">{userData.middleName || "Not set"}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="form-label text-sm">Surname</label>
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <span className="text-neutral-900">{userData.surname || "Not set"}</span>
                    </div>
                  </div>
                  <div>
                    <label className="form-label text-sm">LRN</label>
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <span className="text-neutral-900">{userData.lrn || "Not set"}</span>
                    </div>
                  </div>
                  <div>
                    <label className="form-label text-sm">Grade/Year Level</label>
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <span className="text-neutral-900">{userData.gradeYearLevel || "Not set"}</span>
                    </div>
                  </div>
                  <div>
                    <label className="form-label text-sm">Section</label>
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <span className="text-neutral-900">{userData.section || "Not set"}</span>
                    </div>
                  </div>
                  <div>
                    <label className="form-label text-sm">Contact Number</label>
                    <div className="p-3 bg-neutral-50 rounded-lg flex items-center gap-2">
                      <Phone className="w-4 h-4 text-neutral-500" />
                      <span className="text-neutral-900">{userData.contactNumber || "Not set"}</span>
                    </div>
                  </div>
                </div>
              ) : (
                // Edit mode
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label text-sm">First Name *</label>
                      <input
                        type="text"
                        value={profileForm.firstName}
                        onChange={(e) => handleProfileChange("firstName", e.target.value)}
                        className="form-input"
                        placeholder="Enter first name"
                      />
                      {profileErrors.firstName && (
                        <p className="text-red-500 text-xs mt-1">{profileErrors.firstName}</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label text-sm">Middle Name</label>
                      <input
                        type="text"
                        value={profileForm.middleName}
                        onChange={(e) => handleProfileChange("middleName", e.target.value)}
                        className="form-input"
                        placeholder="Enter middle name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="form-label text-sm">Surname *</label>
                    <input
                      type="text"
                      value={profileForm.surname}
                      onChange={(e) => handleProfileChange("surname", e.target.value)}
                      className="form-input"
                      placeholder="Enter surname"
                    />
                    {profileErrors.surname && (
                      <p className="text-red-500 text-xs mt-1">{profileErrors.surname}</p>
                    )}
                  </div>
                  <div>
                    <label className="form-label text-sm">LRN *</label>
                    <input
                      type="text"
                      value={profileForm.lrn}
                      onChange={(e) => handleProfileChange("lrn", e.target.value)}
                      className="form-input"
                      placeholder="12-digit LRN"
                      maxLength="12"
                    />
                    {profileErrors.lrn && (
                      <p className="text-red-500 text-xs mt-1">{profileErrors.lrn}</p>
                    )}
                  </div>
                  <div>
                    <label className="form-label text-sm">Grade/Year Level *</label>
                    <select
                      value={profileForm.gradeYearLevel}
                      onChange={(e) => handleProfileChange("gradeYearLevel", e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select grade/year level</option>
                      <option value="Grade 7">Grade 7</option>
                      <option value="Grade 8">Grade 8</option>
                      <option value="Grade 9">Grade 9</option>
                      <option value="Grade 10">Grade 10</option>
                      <option value="Grade 11">Grade 11</option>
                      <option value="Grade 12">Grade 12</option>
                      <option value="1st Year">1st Year</option>
                      <option value="2nd Year">2nd Year</option>
                      <option value="3rd Year">3rd Year</option>
                      <option value="4th Year">4th Year</option>
                    </select>
                    {profileErrors.gradeYearLevel && (
                      <p className="text-red-500 text-xs mt-1">{profileErrors.gradeYearLevel}</p>
                    )}
                  </div>
                  <div>
                    <label className="form-label text-sm">Section *</label>
                    <input
                      type="text"
                      value={profileForm.section}
                      onChange={(e) => handleProfileChange("section", e.target.value)}
                      className="form-input"
                      placeholder="Enter your section (e.g., A, B, 1, 2)"
                    />
                    {profileErrors.section && (
                      <p className="text-red-500 text-xs mt-1">{profileErrors.section}</p>
                    )}
                  </div>
                  <div>
                    <label className="form-label text-sm">Contact Number *</label>
                    <input
                      type="tel"
                      value={profileForm.contactNumber}
                      onChange={(e) => handleProfileChange("contactNumber", e.target.value)}
                      className="form-input"
                      placeholder="09XXXXXXXXX or +639XXXXXXXXX"
                    />
                    {profileErrors.contactNumber && (
                      <p className="text-red-500 text-xs mt-1">{profileErrors.contactNumber}</p>
                    )}
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => {
                        setEditingProfile(false);
                        setProfileErrors({});
                        // Reset form to current data
                        setProfileForm({
                          firstName: userData.firstName || "",
                          middleName: userData.middleName || "",
                          surname: userData.surname || "",
                          lrn: userData.lrn || "",
                          gradeYearLevel: userData.gradeYearLevel || "",
                          section: userData.section || "",
                          contactNumber: userData.contactNumber || "",
                        });
                      }}
                      className="btn-secondary flex-1 py-3"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleProfileUpdate}
                      disabled={Object.keys(profileErrors).length > 0}
                      className="btn-primary flex-1 py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      Save Changes
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Update Name */}
          <div className="card">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-primary-600" />
              Update Profile
            </h2>
            <div className="space-y-4">
              <div>
                <label className="form-label">Display Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="form-input"
                  placeholder="Enter your name"
                />
              </div>
              <button
                onClick={handleNameUpdate}
                className="btn-primary w-full py-3 flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Name
              </button>
            </div>
          </div>

          {/* Update Password */}
          <div className="card">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
              <Key className="w-5 h-5 text-warning-600" />
              Security
            </h2>
            <div className="space-y-4">
              <div>
                <label className="form-label">Current Password</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="form-input pr-10"
                    placeholder="Enter your current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPassword ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>
              <div>
                <label className="form-label">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="form-input pr-10"
                    placeholder="Enter new password (min 8 chars, letter, number, symbol)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>

              {/* Password Strength Checklist */}
              {newPassword && (
                <div className="space-y-2 p-3 bg-neutral-50 rounded-lg">
                  <p className="text-sm font-medium text-neutral-700">Password Requirements:</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      {passwordStrength.has8Chars ? (
                        <Check className="w-4 h-4 text-green-500 animate-pulse" />
                      ) : (
                        <div className="w-4 h-4 border border-gray-300 rounded-full"></div>
                      )}
                      <span className={passwordStrength.has8Chars ? "text-green-700" : "text-neutral-500"}>
                        8 Characters minimum
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {passwordStrength.hasLetter ? (
                        <Check className="w-4 h-4 text-green-500 animate-pulse" />
                      ) : (
                        <div className="w-4 h-4 border border-gray-300 rounded-full"></div>
                      )}
                      <span className={passwordStrength.hasLetter ? "text-green-700" : "text-neutral-500"}>
                        Contains letters
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {passwordStrength.hasNumber ? (
                        <Check className="w-4 h-4 text-green-500 animate-pulse" />
                      ) : (
                        <div className="w-4 h-4 border border-gray-300 rounded-full"></div>
                      )}
                      <span className={passwordStrength.hasNumber ? "text-green-700" : "text-neutral-500"}>
                        Contains numbers
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {passwordStrength.hasSymbol ? (
                        <Check className="w-4 h-4 text-green-500 animate-pulse" />
                      ) : (
                        <div className="w-4 h-4 border border-gray-300 rounded-full"></div>
                      )}
                      <span className={passwordStrength.hasSymbol ? "text-green-700" : "text-neutral-500"}>
                        Contains symbols
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="form-label">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="form-input pr-10"
                    placeholder="Confirm your new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>
              <button
                onClick={handlePasswordChange}
                className="btn-warning w-full py-3 flex items-center justify-center gap-2"
              >
                <Key className="w-4 h-4" />
                Update Password
              </button>
            </div>
          </div>


        </div>
      </div>
    </div>
  );
}
